import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { shortDate } from '../format';
import { SignaturePad } from './SignaturePad';
import type { SignerView } from '../types';

type Step = 'read' | 'identify' | 'sign' | 'done';

type Reading = { scrolledToEnd: boolean; seconds: number };

/**
 * Page publique de signature, en quatre temps : lire, s'identifier, signer,
 * confirmer. Le défilement et le temps de lecture sont suivis document par
 * document : c'est ce qui fait la différence entre un consentement établi et
 * une case cochée.
 */
export function Sign({ onLeave }: { onLeave: () => void }) {
  const [view, setView] = useState<SignerView | null>(null);
  const [step, setStep] = useState<Step>('read');
  const [readings, setReadings] = useState<Record<string, Reading>>({});
  const [openRole, setOpenRole] = useState<string>('');
  const [birthDate, setBirthDate] = useState('');
  const [code, setCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [kind, setKind] = useState<'TYPED' | 'DRAWN'>('TYPED');
  const [typedName, setTypedName] = useState('');
  const [drawn, setDrawn] = useState<string | null>(null);
  const [mention, setMention] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [signedOn, setSignedOn] = useState<string | null>(null);
  const openedAt = useRef<number>(0);

  async function load(): Promise<void> {
    try {
      const result = await api<SignerView>('/api/signature/envelope');
      setView(result);
      if (result.alreadySigned) {
        setStep('done');
        setSignedOn(result.signedOn);
      } else if (result.identified) {
        setStep('sign');
      }
      if (!openRole && result.documents[0]) setOpenRole(result.documents[0].role);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Lien invalide.');
    }
  }

  useEffect(() => { void load(); }, []);

  /**
   * Compte le temps réellement passé sur le document ouvert, seconde par
   * seconde. Le compteur ne s'incrémentait qu'au changement d'onglet : rester
   * sur le dernier document laissait le bouton de signature grisé pour
   * toujours, quel que soit le temps passé dessus.
   */
  useEffect(() => {
    if (!openRole) return undefined;
    openedAt.current = Date.now();
    const tick = window.setInterval(() => {
      setReadings((current) => ({
        ...current,
        [openRole]: {
          scrolledToEnd: current[openRole]?.scrolledToEnd ?? false,
          seconds: (current[openRole]?.seconds ?? 0) + 1,
        },
      }));
    }, 1000);
    return () => window.clearInterval(tick);
  }, [openRole]);

  const allRead = useMemo(() => {
    if (!view) return false;
    return view.documents.every((document) => {
      const reading = readings[document.role];
      return reading?.scrolledToEnd && reading.seconds >= view.readingSeconds;
    });
  }, [view, readings]);

  function markScrolled(role: string): void {
    setReadings((current) => ({
      ...current,
      [role]: { scrolledToEnd: true, seconds: current[role]?.seconds ?? 0 },
    }));
  }

  async function requestCode(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ maskedEmail: string }>('/api/signature/otp', {
        method: 'POST',
        body: JSON.stringify({ birthDate }),
      });
      setMaskedEmail(result.maskedEmail);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Identification impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await api('/api/signature/otp/verify', { method: 'POST', body: JSON.stringify({ code: code.trim() }) });
      setStep('sign');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Code refusé.');
    } finally {
      setBusy(false);
    }
  }

  async function sign(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const finalReadings = readings;
      const result = await api<{ signedAt: string }>('/api/signature/sign', {
        method: 'POST',
        body: JSON.stringify({
          kind,
          value: kind === 'TYPED' ? typedName : '',
          ...(kind === 'DRAWN' && drawn ? { dataUrl: drawn } : {}),
          mention,
          readings: finalReadings,
        }),
      });
      setSignedOn(result.signedAt);
      setStep('done');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Signature refusée.');
    } finally {
      setBusy(false);
    }
  }

  if (!view) {
    return (
      <main className="center-page">
        {error
          ? <section className="message-card error-card"><h1>Ce lien ne peut pas être ouvert.</h1><p>{error}</p></section>
          : <div className="loading-mark" aria-label="Chargement"><span /><span /><span /></div>}
      </main>
    );
  }

  if (step === 'done') {
    return (
      <main className="center-page">
        <section className="message-card">
          <p className="eyebrow">C'est fait</p>
          <h1>Vos documents sont signés.</h1>
          <p>
            {signedOn ? `Signature enregistrée le ${shortDate(signedOn.slice(0, 10))}. ` : ''}
            Vous les recevez par e-mail avec le certificat de preuve, qui atteste de la signature et de
            l'intégrité des fichiers.
          </p>
          <a className="button primary" href="/api/signature/certificate" target="_blank" rel="noreferrer">
            Ouvrir le certificat de preuve
          </a>
          <button className="button secondary" onClick={onLeave}>Fermer</button>
        </section>
      </main>
    );
  }

  return (
    <div className="sign-shell">
      <header className="sign-header">
        <p className="eyebrow">Signature électronique</p>
        <h1>{view.signer.firstName}, {view.documents.length > 1 ? 'vos documents' : 'votre document'} à signer</h1>
        <p className="sign-context">
          Chambre {view.roomName} · bailleur {view.landlord} · lien valable jusqu'au {shortDate(view.expiresOn)}
        </p>
        <ol className="sign-steps">
          <li className={step === 'read' ? 'active' : 'done'}>Lire</li>
          <li className={step === 'identify' ? 'active' : step === 'sign' ? 'done' : ''}>S'identifier</li>
          <li className={step === 'sign' ? 'active' : ''}>Signer</li>
        </ol>
      </header>

      <main className="sign-body">
        {error && <div className="inline-message error" role="alert">{error}</div>}

        {step === 'read' && (
          <section className="panel">
            <h2>Lisez chaque document en entier</h2>
            <p className="section-intro">
              Faites défiler chaque document jusqu'au bout. Le bouton de signature s'active une fois tous les
              documents parcourus, après {view.readingSeconds} secondes de lecture minimum sur chacun.
            </p>
            <div className="tab-bar">
              {view.documents.map((document) => {
                const reading = readings[document.role];
                const ok = reading?.scrolledToEnd && reading.seconds >= view.readingSeconds;
                return (
                  <button
                    key={document.role}
                    className={document.role === openRole ? 'active' : ''}
                    onClick={() => setOpenRole(document.role)}
                  >
                    {ok ? '✓ ' : ''}{document.title}
                  </button>
                );
              })}
            </div>
            {openRole && (
              <>
                <object
                  className="sign-document"
                  data={`/api/signature/documents/${openRole}`}
                  type="application/pdf"
                  aria-label="Document à lire"
                >
                  <p>
                    Votre navigateur n'affiche pas les PDF.{' '}
                    <a href={`/api/signature/documents/${openRole}`} target="_blank" rel="noreferrer">
                      Ouvrir le document
                    </a>
                  </p>
                </object>
                <label className="check-field">
                  <input
                    type="checkbox"
                    checked={readings[openRole]?.scrolledToEnd ?? false}
                    onChange={() => markScrolled(openRole)}
                  />
                  <span>J'ai lu ce document en entier.</span>
                </label>
                {(() => {
                  // Dire ce qui manque encore, plutôt que de laisser le bouton
                  // grisé sans explication.
                  const reading = readings[openRole];
                  const left = Math.max(0, view.readingSeconds - Math.floor(reading?.seconds ?? 0));
                  if (!reading?.scrolledToEnd) {
                    return <p className="muted">Cochez la case ci-dessus une fois le document parcouru.</p>;
                  }
                  if (left > 0) {
                    return <p className="muted">Encore {left} seconde{left > 1 ? 's' : ''} de lecture sur ce document.</p>;
                  }
                  return <p className="muted">Ce document est validé.</p>;
                })()}
              </>
            )}
            <div className="record-actions">
              <button className="button primary" disabled={!allRead} onClick={() => setStep('identify')}>
                {allRead ? 'Continuer' : 'Lisez tous les documents pour continuer'}
              </button>
            </div>
          </section>
        )}

        {step === 'identify' && (
          <section className="panel">
            <h2>Confirmez votre identité</h2>
            <p className="section-intro">
              Deux vérifications : votre date de naissance, connue du seul dossier de location, puis un code
              envoyé à votre adresse e-mail.
            </p>
            <div className="form-grid">
              <label>Votre date de naissance
                <input type="date" value={birthDate} onChange={(event) => setBirthDate(event.target.value)} />
              </label>
            </div>
            <div className="record-actions">
              <button className="button secondary" disabled={busy || !birthDate} onClick={() => void requestCode()}>
                {maskedEmail ? 'Renvoyer un code' : 'Recevoir mon code'}
              </button>
            </div>
            {maskedEmail && (
              <>
                <p className="section-intro">
                  Code envoyé à {maskedEmail}. Il est valable {view.otpMinutes} minutes.
                </p>
                <div className="form-grid">
                  <label>Code à 6 chiffres
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                    />
                  </label>
                </div>
                <div className="record-actions">
                  <button className="button primary" disabled={busy || code.length < 6} onClick={() => void verifyCode()}>
                    Valider le code
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        {step === 'sign' && (
          <section className="panel">
            <h2>Signez</h2>
            <div className="tab-bar">
              <button className={kind === 'TYPED' ? 'active' : ''} onClick={() => setKind('TYPED')}>
                Saisir mon nom
              </button>
              <button className={kind === 'DRAWN' ? 'active' : ''} onClick={() => setKind('DRAWN')}>
                Signer au doigt
              </button>
            </div>
            {kind === 'TYPED' ? (
              <div className="form-grid">
                <label>Vos prénom et nom
                  <input
                    type="text"
                    placeholder={`${view.signer.firstName} ${view.signer.lastName}`}
                    value={typedName}
                    onChange={(event) => setTypedName(event.target.value)}
                  />
                </label>
              </div>
            ) : (
              <SignaturePad
                title="Votre signature"
                initialName={`${view.signer.firstName} ${view.signer.lastName}`}
                saved={drawn !== null}
                onSave={async (_name, dataUrl) => { setDrawn(dataUrl); }}
              />
            )}
            <div className="form-grid">
              <label>Recopiez « Lu et approuvé »
                <input type="text" value={mention} onChange={(event) => setMention(event.target.value)} />
              </label>
            </div>
            <div className="record-actions">
              <button
                className="button primary"
                disabled={busy || !mention || (kind === 'TYPED' ? !typedName : !drawn)}
                onClick={() => void sign()}
              >
                Signer {view.documents.length > 1 ? 'les documents' : 'le document'}
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
